import cn from 'classnames';
import { useRouter } from 'next/router';

import { hasItems } from 'src/utils/hasItems';

import DWLogo from 'assets/icons/dw-logo.svg';
import Facebook from 'assets/icons/facebook.svg';
import Instagram from 'assets/icons/instagram.svg';
import Linkedin from 'assets/icons/linkedin.svg';
import TwitterX from 'assets/icons/twitter-x.svg';
import YouTube from 'assets/icons/youtube.svg';

import Cta from 'components/BaseHelpers/Cta';
import Link from 'components/BaseHelpers/Link';
import RichText from 'components/BaseHelpers/RichText';
import StartIndex from 'components/BaseHelpers/StartIndex';
import StopIndex from 'components/BaseHelpers/StopIndex';
import Text from 'components/BaseHelpers/Text';

import { ComponentProps } from '../../../types/component-props';
import { CoreGlobalNavigationFooterData } from '../../../types/components';
import PrimaryGroups from './components/PrimaryGroups';

import styles from './CoreGlobalNavigationFooter.module.scss';

export type CoreGlobalNavigationFooterProps = ComponentProps & CoreGlobalNavigationFooterData;

const CoreGlobalNavigationFooter = (props: CoreGlobalNavigationFooterProps): JSX.Element => {
  const router = useRouter();

  const {
    primaryNavigationGroups,
    secondaryNavigation,
    tertiaryNavigation,
    copyrights,
    socialMedia,
    subscribeCta,
  } = props.fields;

  return (
    <>
      <StopIndex />
      <footer className={styles.main}>
        <div className={cn(styles.container, 'container-12')}>
          {router.pathname === '/' ? (
            <span className={styles['logo-wrapper']}>
              <DWLogo className={styles.logo} />
            </span>
          ) : (
            <Link href="/" className={styles['logo-wrapper']}>
              <DWLogo className={styles.logo} />
            </Link>
          )}

          <div className={styles['top-section']}>
            <PrimaryGroups groups={primaryNavigationGroups} />
            <div className={styles['social-media-subscribe']}>
              {hasItems(socialMedia.links) && (
                <div className={styles['social-media']}>
                  <Text className={'body-eyebrow'} field={socialMedia.label} />
                  <ul className={styles['social-links']}>
                    {socialMedia.links.map((link, index) => (
                      <li key={index}>
                        <Link
                          href={link.href}
                          openNewTab
                          className={styles['social-link']}
                          aria-label={link.platform}
                        >
                          {link.platform === 'facebook' ? (
                            <Facebook className={cn(styles['social-icon'], styles.facebook)} />
                          ) : link.platform === 'x' ? (
                            <TwitterX className={cn(styles['social-icon'], styles.x)} />
                          ) : link.platform === 'instagram' ? (
                            <Instagram className={cn(styles['social-icon'], styles.instagram)} />
                          ) : link.platform === 'linkedin' ? (
                            <Linkedin className={cn(styles['social-icon'], styles.linkedin)} />
                          ) : link.platform === 'youtube' ? (
                            <YouTube className={cn(styles['social-icon'], styles.youtube)} />
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className={styles['subscribe-cta']}>
                <Text className={'heading-h5'} field={subscribeCta.header} />
                <RichText
                  className={cn('body-med', styles['subscribe-description'])}
                  field={subscribeCta.description}
                />
                <Cta
                  tag="a"
                  href={subscribeCta.link.href}
                  url={subscribeCta.link.url}
                  className={styles['subscribe-link']}
                  theme="dark"
                >
                  {subscribeCta.link.label}
                </Cta>
              </div>
            </div>
          </div>
          <div className={styles['bottom-section']}>
            {hasItems(secondaryNavigation) && (
              <ul className={styles['secondary-navigation']}>
                {secondaryNavigation.map((link, index) => (
                  <li className={styles['secondary-item']} key={index}>
                    <Link
                      href={link.href}
                      url={link.url}
                      openNewTab={link.target === '_blank'}
                      className={cn(styles['secondary-link'], 'body-eyebrow')}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {hasItems(tertiaryNavigation) && (
              <ul className={styles['tertiary-navigation']}>
                {tertiaryNavigation.map((link, index) => (
                  <li className={styles['tertiary-item']} key={index}>
                    <Link
                      href={link.href}
                      url={link.url}
                      openNewTab={link.target === '_blank'}
                      className={cn(styles['tertiary-link'], 'body-sm')}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {hasItems(copyrights) && (
              <div className={styles['copyrights']}>
                {copyrights.map((text, index) => (
                  <Text tag="p" className={'caption-2'} field={text} key={index} />
                ))}
              </div>
            )}
          </div>
        </div>
      </footer>
      <StartIndex />
    </>
  );
};

export default CoreGlobalNavigationFooter;
